// components/DegreeAudit/Progress/ProgressFormulaPopover.tsx
//
// Popover that explains how progress percentage is calculated. Renders via
// React Portal so it escapes parent stacking contexts.

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import { useClickOutside } from '../../../hooks/useClickOutside';

export interface ProgressFormulaPopoverProps {
  creditsCompleted?: number;
  creditsInProgress?: number;
  creditsPlanned?: number;
  creditsRequired?: number;
  progress?: number;
  label?: string;
  colorClass?: string;
}

const ProgressFormulaPopover = ({
  creditsCompleted = 0,
  creditsInProgress = 0,
  creditsPlanned = 0,
  creditsRequired = 120,
  progress = 0,
  label = 'Progress',
  colorClass = 'text-blue-700',
}: ProgressFormulaPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  useClickOutside([popoverRef, buttonRef], () => setIsOpen(false), isOpen);

  const weightedCompleted = creditsCompleted * 1.0;
  const weightedInProgress = creditsInProgress * 0.6;
  const weightedPlanned = creditsPlanned * 0.3;
  const totalWeighted = weightedCompleted + weightedInProgress + weightedPlanned;

  const formatNumber = (num: number): string => {
    if (Number.isInteger(num)) return num.toString();
    return num.toFixed(1).replace(/\.0$/, '');
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`text-xs ${colorClass} hover:underline cursor-pointer inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-300 rounded`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span>
          {label} progress: {Math.round((progress || 0) * 10) / 10}%
        </span>
        <Info className="w-3 h-3 opacity-60" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed w-72 rounded-lg shadow-xl border border-gray-200 p-4"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
              backgroundColor: 'white',
              zIndex: 9999,
            }}
            role="dialog"
            aria-label="Progress formula explanation"
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <h4 className="text-sm font-semibold text-gray-800 mb-3">Progress Calculation</h4>

            <div className="space-y-2 text-xs">
              <div className="bg-gray-50 rounded p-2 mb-3">
                <div className="font-medium text-gray-700 mb-1">Credit Weights:</div>
                <div className="grid grid-cols-3 gap-1 text-gray-600">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span>Completed: 100%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <span>In Progress: 60%</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                    <span>Planned: 30%</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5 font-mono text-gray-700">
                {creditsCompleted > 0 && (
                  <div className="flex justify-between">
                    <span className="text-green-700">{creditsCompleted} × 100%</span>
                    <span>= {formatNumber(weightedCompleted)}</span>
                  </div>
                )}
                {creditsInProgress > 0 && (
                  <div className="flex justify-between">
                    <span className="text-blue-700">{creditsInProgress} × 60%</span>
                    <span>= {formatNumber(weightedInProgress)}</span>
                  </div>
                )}
                {creditsPlanned > 0 && (
                  <div className="flex justify-between">
                    <span className="text-yellow-700">{creditsPlanned} × 30%</span>
                    <span>= {formatNumber(weightedPlanned)}</span>
                  </div>
                )}

                <div className="border-t border-gray-200 my-1"></div>

                <div className="flex justify-between font-medium">
                  <span>Weighted credits</span>
                  <span>{formatNumber(totalWeighted)}</span>
                </div>

                <div className="flex justify-between text-gray-600">
                  <span>Credits required</span>
                  <span>{creditsRequired}</span>
                </div>

                <div className="border-t border-gray-200 my-1"></div>

                <div className="flex justify-between font-semibold text-gray-800">
                  <span>
                    {formatNumber(totalWeighted)} ÷ {creditsRequired} × 100
                  </span>
                  <span className={colorClass}>= {Math.round((progress || 0) * 10) / 10}%</span>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ProgressFormulaPopover;
